import { ActionType, DeviceInfo, EventLogInfo, EventLogResult, EventType, Event, LastSensorReading, LogicOperator, MediaActionsOptionsModel, PTZCameraPreset, Sensor, SensorGroup, SensorGroupInfo, SensorReadingType, TimerEventType, TimeZoneDisplay, TimingInterval, UserGroup } from '@models';
import { ActionQueueLive } from '@models/ActionQueue/ActionQueueLive.model';
import { SensorReadingInfo } from '@models/EventLog/SensorReadingInfo.model';
import { GeoMapItem } from '@models/Map/geoMapItem.model';
import { EventMapper } from 'app/components/trigger-settings/trigger.mapping';
import { isObject } from 'lodash';
import { isBoolean, isDate, isNumber } from 'lodash-es';
import moment from 'moment';
import { SensorMapping } from './sensorMapping';

export interface CommonShared {
    prepareProperty4String(value: any): string;
    compareProperty(label: string, origVal: string, newVal: string): string;
}

export class CommonShared {
  
  public dateColorKeyGreen = '#64a70b';
  public dateColorKeyYellow = '#dadb26';
  public dateColorKeyOrange = '#ff9551';
  public dateColorKeyPurple = 'purple';
  public dateColorKeyRed = '#f93232';

  private eventMapper = new EventMapper();
  public sensorMapper = new SensorMapping();
  
  constructor() {}


  isValidDate(stringDate: string) {
    return !isNaN(Date.parse(stringDate));
  }


  // Colors for the GPS-source indicator, shared by the indicator icons and the
  // map pins:  readings (lat/long types) = blue, reading (own gps) = red,
  // sensor (sensor's gps) = green.
  public gpsSourceColors = {
    readings: '#046dd6',
    reading: '#d32f2f',
    sensor: '#2e7d32',
    none: '#b0b0b0'
  };


  // Table GPS pin: red only when the reading itself carries GPS (not derived from lat/long readings or the sensor).
  readingHasDirectGps(r: SensorReadingInfo): boolean {
    return r.ownGpsLocation != undefined;
  }


  readingGpsPinColor(r: SensorReadingInfo): string {
    return this.readingHasDirectGps(r) ? this.gpsSourceColors.reading : this.gpsSourceColors.none;
  }


  readingGpsPinTooltip(r: SensorReadingInfo): string {
    if (!this.readingHasDirectGps(r)) {
      return 'No GPS for this reading';
    }
    const g = r.ownGpsLocation;
    return `Lat: ${g.gpsLat}\nLong: ${g.gpsLong}\nAlt: ${g.gpsAlt ?? 0}`;
  }


  // Header colors for a map pin's info window: the bar matches the pin's own
  // color and the strip above it is a darker shade, mirroring the app's modal
  // headers (an #a9d18e bar over a #548235 strip).
  public gpsSourceHeaderColors = {
    readings: { bar: '#046dd6', strip: '#034a91' },
    reading:  { bar: '#d32f2f', strip: '#8f2020' },
    sensor:   { bar: '#2e7d32', strip: '#1e5320' },
    none:     { bar: '#909090', strip: '#6b6b6b' }
  };


  gpsPinHeaderColors(source: 'reading' | 'readings' | 'sensor' | 'none'): { bar: string; strip: string } {
    return this.gpsSourceHeaderColors[source] ?? this.gpsSourceHeaderColors.reading;
  }


  // ---- Map pin info windows (draggable, with a leader line to the pin) ----
  //
  // The info windows on the GeoMaps can be dragged by their colored header so a
  // popup stops covering neighboring pins. What actually moves is Google's own
  // container (.gm-style-iw-c), named by cdkDragRootElement on the popup.
  //
  // The leader line that ties a dragged popup back to its pin is NOT drawn in the
  // info-window DOM (that fights Google's sizing and clipping). It is drawn on the
  // map itself by a google.maps.OverlayView - see updatePinPopupLeader and
  // createPinLeaderOverlay below.

  // Each dragged popup owns its own leader overlay (stored on the GeoMapItem), so
  // several open popups can each show a line to their pin at once. This registry
  // tracks every overlay this map has created so they can all be torn down when
  // the marker set is rebuilt (clearAllPinPopupLeaders).
  private pinLeaderOverlays: any[] = [];

  // Google builds .gm-style-iw-c only when the window opens, so cdkDrag cannot
  // resolve it at init - it would fall back to dragging our content inside
  // Google's fixed pane. Hand it the selector just after opening instead, which
  // re-runs cdkDrag's root-element lookup. Resetting dragOffset to zero also
  // resets the CDK transform (it is bound to cdkDragFreeDragPosition), so a
  // reopened popup starts back at its pin. This popup's own leader (if any) is
  // hidden to match, leaving other popups' leaders untouched.
  openPinPopup(infoWindow: { open: (anchor: any) => void }, marker: any, geoMapItem: GeoMapItem): void {
    geoMapItem.isOpen = true;
    geoMapItem.dragOffset = { x: 0, y: 0 };
    geoMapItem.dragRootSelector = '';

    this.hidePinPopupLeader(geoMapItem);

    infoWindow.open(marker);

    setTimeout(() => geoMapItem.dragRootSelector = '.gm-style-iw-c');
  }


  // Reopen a popup that was saved (open, possibly dragged away from its pin).
  //
  // Opening goes through openPinPopup, then the saved drag offset is applied the same
  // way a real drag would: once Google has built .gm-style-iw-c, promote its computed
  // transform inline (pinPopupDragStarted, so CDK folds the offset in instead of
  // replacing Google's anchor translate), set the offset, and finally draw the leader
  // line. Auto-pan is switched off while restoring, otherwise opening each popup would
  // pan the map away from the saved center; it is switched back on afterwards.
  restorePinPopup(infoWindow: any, marker: any, geoMapItem: GeoMapItem, dragOffset: { x: number; y: number } | undefined, map: any): void {
    const nativeInfoWindow = infoWindow?.infoWindow;
    nativeInfoWindow?.setOptions?.({ disableAutoPan: true });

    this.openPinPopup(infoWindow, marker, geoMapItem);

    const offset = dragOffset ?? { x: 0, y: 0 };

    const applyOffset = (attempt: number) => {
      const content = nativeInfoWindow?.getContent?.() as Element | undefined;
      const popupEl = content?.closest?.('.gm-style-iw-c') as HTMLElement | null | undefined;

      if (popupEl == undefined) {
        if (attempt < 30) {
          setTimeout(() => applyOffset(attempt + 1), 50);
        }
        return;
      }

      if (offset.x !== 0 || offset.y !== 0) {
        this.pinPopupDragStarted(popupEl);
        geoMapItem.dragRootSelector = '.gm-style-iw-c';
        geoMapItem.dragOffset = { x: offset.x, y: offset.y };

        // After change detection has handed the offset to cdkDrag.
        setTimeout(() => this.updatePinPopupLeader(geoMapItem, offset, map, popupEl));
      }
    };

    setTimeout(() => applyOffset(0));
    setTimeout(() => nativeInfoWindow?.setOptions?.({ disableAutoPan: false }), 2000);
  }


  // Stop the popup from jumping on the first drag.
  //
  // Google positions .gm-style-iw-c (our drag root) with a translate(-50%,-100%)
  // that lives in Google's injected stylesheet, not as an inline style, so its
  // bottom-center sits on the pin. CDK preserves an existing transform by reading
  // only the element's INLINE transform (_applyRootElementTransform), so it never
  // sees Google's rule: it captures an empty initial transform and its own
  // translate3d overrides the -50%/-100%, dropping the popup by its own height and
  // right by half its width on the first move.
  //
  // Fix: at drag start (fired before CDK applies any transform), copy Google's
  // computed transform onto the element as an inline style. CDK then folds its
  // drag translate in front of it (combineTransforms) instead of replacing it, so
  // the anchor offset is preserved and there is no jump. dragEl is the drag root
  // from cdkDragStarted's $event.source.getRootElement().
  pinPopupDragStarted(dragEl: HTMLElement): void {
    if (dragEl == undefined || typeof window == 'undefined') {
      return;
    }

    // Only promote it once; on later drags the inline transform is already CDK's
    // combined value and re-reading the computed style would double the offset.
    if (dragEl.style.transform != undefined && dragEl.style.transform !== '') {
      return;
    }

    const computed = window.getComputedStyle(dragEl).transform;

    if (computed != undefined && computed !== 'none') {
      dragEl.style.transform = computed;
    }
  }


  // Draw (or update) this popup's leader line back to its pin.
  //
  // The line is drawn on the map by an OverlayView, not inside the popup. Rather
  // than infer the popup's position from the drag offset (which broke when the
  // no-jump fix changed the popup's transform stack), the overlay MEASURES the
  // popup element's real screen rectangle and draws from the pin to the popup's
  // bottom-center. That is immune to whatever transforms Google and CDK apply.
  // The overlay redraws on every map pan/zoom, re-measuring both ends, so the line
  // keeps tracking the popup and pin for free.
  //
  // Each popup keeps its own overlay on geoMapItem.leaderOverlay so multiple
  // dragged popups show their lines simultaneously.
  //
  // map is the native google.maps.Map (from the GoogleMap component's `map`
  // ViewChild: map.googleMap); popupEl is the dragged popup container
  // (.gm-style-iw-c), from cdkDragEnded's $event.source.getRootElement().
  updatePinPopupLeader(geoMapItem: GeoMapItem, position: { x: number; y: number }, map: any, popupEl: HTMLElement): void {
    geoMapItem.dragOffset = { x: position.x, y: position.y };

    if (map == undefined || typeof google == 'undefined' || google.maps?.OverlayView == undefined) {
      return;
    }

    if (geoMapItem.leaderOverlay == undefined) {
      geoMapItem.leaderOverlay = this.createPinLeaderOverlay();
      geoMapItem.leaderOverlay.setMap(map);
      this.pinLeaderOverlays.push(geoMapItem.leaderOverlay);
    }

    const dragged = (position.x !== 0 || position.y !== 0);

    // White, per design, so it reads clearly over the satellite imagery.
    geoMapItem.leaderOverlay.setLeader(new google.maps.LatLng(geoMapItem.gpsLat, geoMapItem.gpsLong), popupEl, dragged, '#ffffff');
  }


  // Hide one popup's leader (e.g. when that popup is closed or reopened). The
  // overlay stays attached to the map, just drawing nothing, ready for the next
  // drag; other popups' leaders are unaffected.
  hidePinPopupLeader(geoMapItem: GeoMapItem): void {
    if (geoMapItem?.leaderOverlay != undefined) {
      geoMapItem.leaderOverlay.hide();
    }
  }


  // Detach and forget every leader overlay. Call when the marker set is rebuilt
  // (new selection / date range), otherwise lines from the previous set linger on
  // the map since the overlays live on Google's map, not in Angular's DOM.
  clearAllPinPopupLeaders(): void {
    this.pinLeaderOverlays.forEach(overlay => overlay.setMap(null));
    this.pinLeaderOverlays = [];
  }


  // Build one leader overlay. Defined here (not as a top-level class) because
  // google.maps.OverlayView is only available after the Maps API has loaded. The
  // line is a thin div in the overlayLayer pane - above the map tiles but below
  // the markers and the info window, so it can never cover a pin or popup content.
  private createPinLeaderOverlay(): any {
    const overlay: any = new google.maps.OverlayView();

    const el = document.createElement('div');
    el.className = 'kig-pin-popup-leader';
    el.style.position = 'absolute';
    el.style.height = '2px';
    el.style.transformOrigin = '0 50%';   // rotate about the pin end (its left edge)
    el.style.pointerEvents = 'none';
    el.style.display = 'none';

    let pin: any;
    let popupEl: HTMLElement | undefined;
    let visible = false;
    let color = '#ffffff';

    // Pixel offset from the pin's lat/lng anchor to the popup's bottom-center,
    // measured once per drag (needsMeasure) and then held. See draw() for why it
    // must not be re-measured every frame.
    let popupOffset = { x: 0, y: 0 };
    let needsMeasure = false;

    // The pin icon (CommonShared.gpsPinIcon: 45x60, anchor {x:22,y:57}) renders
    // its round head centered ~(-4,-40)px from the lat/lng anchor, so the line
    // should start at the pin's visual center, not the anchor at the base.
    const PIN_CENTER_DX = -4;
    const PIN_CENTER_DY = -40;

    overlay.onAdd = function () {
      this.getPanes().overlayLayer.appendChild(el);
    };

    overlay.onRemove = function () {
      el.remove();
    };

    overlay.draw = function () {
      const projection = this.getProjection();
      const mapDiv = this.getMap()?.getDiv();

      if (!visible || projection == undefined || pin == undefined || popupEl == undefined || mapDiv == undefined) {
        el.style.display = 'none';
        return;
      }

      const pinDiv = projection.fromLatLngToDivPixel(pin);

      // The popup is measured only when its drag ends (needsMeasure), NOT on every
      // pan/zoom draw. Re-measuring during a pan reads getBoundingClientRect while
      // Google is still repositioning the info window mid-animation, so the line
      // snapped to a transient position and jumped around (worst on the 2nd popup,
      // whose draw() interleaves differently with Google's reposition cycle).
      //
      // Instead the pin->popup pixel offset is captured once and reused: the pin,
      // the popup and this leader all live in map panes that translate together,
      // so a pan carries all three rigidly, and Google keeps the info window at a
      // constant pixel offset from its pin across zoom too - so reprojecting just
      // the pin each frame and adding the stored offset keeps both ends glued.
      if (needsMeasure) {
        const mapRect = mapDiv.getBoundingClientRect();
        const popupRect = popupEl.getBoundingClientRect();
        const pinContainer = projection.fromLatLngToContainerPixel(pin);

        // Popup bottom-center minus the pin anchor, both in container pixels. A
        // difference of two positions is the same in container- and div-pixel
        // space (they differ only by the pane's constant offset), so it can be
        // added to pinDiv directly below.
        popupOffset = {
          x: (popupRect.left + (popupRect.width / 2) - mapRect.left) - pinContainer.x,
          y: (popupRect.bottom - mapRect.top) - pinContainer.y
        };
        needsMeasure = false;
      }

      // Line start: the pin's visual center. Line end: the popup's bottom-center,
      // the stored offset from the (reprojected) pin.
      const startX = pinDiv.x + PIN_CENTER_DX;
      const startY = pinDiv.y + PIN_CENTER_DY;
      const endX = pinDiv.x + popupOffset.x;
      const endY = pinDiv.y + popupOffset.y;

      const dx = endX - startX;
      const dy = endY - startY;
      const length = Math.sqrt((dx * dx) + (dy * dy));

      // Screen y grows downward, matching CSS rotate()'s positive sense.
      const degrees = Math.atan2(dy, dx) * 180 / Math.PI;

      el.style.left = startX + 'px';
      el.style.top = startY + 'px';
      el.style.width = length + 'px';
      el.style.backgroundColor = color;
      el.style.transform = 'rotate(' + degrees + 'deg)';
      el.style.display = 'block';
    };

    overlay.setLeader = (pinLatLng: any, popupElement: HTMLElement, isVisible: boolean, lineColor: string) => {
      pin = pinLatLng;
      popupEl = popupElement;
      color = lineColor;
      visible = isVisible;

      // Remeasure the popup's offset for this drag. If getProjection() is not
      // ready yet on this draw, needsMeasure stays set and the measurement happens
      // on the next draw (Google calls draw() again once the projection exists).
      if (isVisible) {
        needsMeasure = true;
      }

      overlay.draw();
    };

    overlay.hide = () => {
      visible = false;
      overlay.draw();
    };

    return overlay;
  }


  // Build a Google Maps marker icon (a location-dot pin with a soft drop shadow)
  // colored by GPS source. Returned as an SVG data URI so it matches the
  // Font Awesome fa-location-dot indicator icon.
  gpsPinIcon(source: 'reading' | 'readings' | 'sensor' | 'none'): any {
    const color = this.gpsSourceColors[source] ?? this.gpsSourceColors.reading;

    // location-dot shape (Font Awesome path) at 24x32, with a blurred shadow.
    const svg =
      `<svg xmlns="http://www.w3.org/2000/svg" width="30" height="40" viewBox="0 0 30 40">` +
        `<defs>` +
          `<filter id="s" x="-40%" y="-20%" width="180%" height="160%">` +
            `<feDropShadow dx="0" dy="1.5" stdDeviation="1.5" flood-color="#000000" flood-opacity="0.45"/>` +
          `</filter>` +
        `</defs>` +
        `<path filter="url(#s)" transform="translate(3,2) scale(0.046875)" fill="${color}" stroke="#ffffff" stroke-width="20" ` +
          `d="M192 0C86 0 0 86 0 192c0 77 27 99 172 309 10 14 30 14 40 0 145-210 172-232 172-309C384 86 298 0 192 0zm0 272a80 80 0 1 1 0-160 80 80 0 0 1 0 160z"/>` +
      `</svg>`;

    return {
      url: 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent(svg),
      scaledSize: { width: 45, height: 60 } as any,
      anchor: { x: 22, y: 57 } as any
    };
  }

  
  getColorCodeForLastReadingDate(sensorReadingDate: Date): string {
    const duration = moment.duration(moment().diff(sensorReadingDate));
    const diffDays = Math.abs(duration.asDays());

    if (diffDays <= 1) {
      return this.dateColorKeyGreen; 
    } else if (diffDays <= 7) {
      return this.dateColorKeyYellow;
    } else if (diffDays <= 30) {
      return this.dateColorKeyOrange;
    } else if (diffDays <= 182) {
      return this.dateColorKeyPurple;
    } else {
      return this.dateColorKeyRed;
    }
  }

  
  getSensorDisplayName(sensor: Sensor): string {
    return (sensor.name != undefined && sensor.name != '') ? sensor.name : sensor.uuid ;
  }
  

  prepareProperty4String(value: any): string {
    if (value == null || value == undefined) {
      return 'null';
    }
    if (isBoolean(value)) {
      return value.toString();
    }
    if (isNumber(value) || isDate(value)) {
      return value.toString();
    }
    if (isObject(value)) {
      return JSON.stringify(value);
    }
    return value;
  }


  compareProperty(label: string, origVal: string, newVal: string): string {
    return (origVal != newVal) ? label + ' (' + origVal + ' -> ' + newVal + '),  ' : '';
  }


  setValuePrecisionForKey(value: number, key: string): number {
    switch (key.toLowerCase()) {
      case 'moisture':
        return Number(value.toPrecision(3));

      case 'battery':
        return Number(value.toPrecision(4));
        
      case 'lastwaterplanelapsedhours':
        return Number(value.toPrecision(2));

      default:
        return value;
    }
  }


  sleep(ms: number) {
    return new Promise(resolve => setTimeout(resolve, ms));
  }
  
  
  sortStringArray(names: string[]) {
    names.sort((a, b) => {
      if (b > a) {
        return -1;
      }
      if (b < a) {
        return 1;
      }
    });
  }


  sortArrayByNameParm(array: any) {
    array.sort((x, y) => {
      if (x.name.toLowerCase() < y.name.toLowerCase()) {
        return -1;
      }
      else if (x.name.toLowerCase() > y.name.toLowerCase()) {
        return 1;
      }
      else return 0;
    });
  }


  sortArrayByUserNameParm(array: any) {
    array.sort((x, y) => {
      if (x.userName.toLowerCase() < y.userName.toLowerCase()) {
        return -1;
      }
      else if (x.userName.toLowerCase() > y.userName.toLowerCase()) {
        return 1;
      }
      else return 0;
    });
  }


  sortArrayByTypeParm(array: any) {
    array.sort((x, y) => {
      if (x.type.toLowerCase() < y.type.toLowerCase()) {
        return -1;
      }
      else if (x.type.toLowerCase() > y.type.toLowerCase()) {
        return 1;
      }
      else return 0;
    });
  }


  getDeviceNameFromSerial(devices: DeviceInfo[], serial: string): string {
    const device = devices.find(d => d.serialNumber === serial);

    if (device === null || device === undefined || device.name === '') {
      return 'unknown';
    }
    return device.name;
  }


  extractTypeFromUUID(uuid: string, btMacAddressPrefixes: {}): string {
    var macAddress = '';

    if (uuid.length >= 17) {    // Check for MET3
      if (uuid.startsWith('MET-')) {
        return 'MET';
      }
    }

    if (uuid.length >= 12) {    // Check for Miflora, Sensorpush, MiThermo and other registered mac prefixes
      macAddress = uuid.substr(uuid.length - 12).toUpperCase();
    }
    else {
      macAddress = uuid.toUpperCase();
    }

    if (btMacAddressPrefixes != undefined) {
      var uuidPrefix = macAddress.substring(0,6);

      for (var key in btMacAddressPrefixes) {
          if (uuidPrefix == key && btMacAddressPrefixes[key].length > 0) {
            return btMacAddressPrefixes[key];
          }
        }
    }

    return 'unknown';
  }


  extractNameFromUUID(uuid: string, btMacAddressPrefixes: {}): string {
    let macAddress = '';

    if (uuid.length >= 12) {    // Check for Miflora, Sensorpush, MiThermo and other registered mac prefixes
      macAddress = uuid.substr(uuid.length - 12).toUpperCase();

      if (btMacAddressPrefixes != undefined) {      
        var uuidPrefix = macAddress.substring(0,6);

        for (var key in btMacAddressPrefixes) {
            if (uuidPrefix == key && btMacAddressPrefixes[key].length > 0) {
              return btMacAddressPrefixes[key][0] + '-' + macAddress.slice(6,8) + ':' + macAddress.slice(8,10) + ':' + macAddress.slice(10,12);
            }
          }
      }
    }

    return '';
  }


  extractMacAddressFromUUID(uuid: string): string {
    let macAddress = '';
    if (uuid.length >= 12) {
      macAddress = uuid.substr(uuid.length - 12).toUpperCase();

      return  macAddress.slice(0, 2) + ':' +
            macAddress.slice(2, 4) + ':' +
            macAddress.slice(4, 6) + ':' +
            macAddress.slice(6, 8) + ':' +
            macAddress.slice(8, 10) + ':' +
            macAddress.slice(10, 12);
    }

    return uuid;
  }


  updateUserInfoForSensorGroup(sensorGroup: SensorGroup, selectedUserGroup: UserGroup, username: string) {
    sensorGroup.userName = username;
    if (selectedUserGroup != null && selectedUserGroup.id > 0) {
      sensorGroup.userGroupId = selectedUserGroup.id;
      sensorGroup.userGroup = selectedUserGroup.name;
    } else {
      sensorGroup.userGroupId = null;
      sensorGroup.userGroup = '';
    }
  }


  cloneSensorGroup(sensorGroup: SensorGroup): SensorGroup {
    const newSensorGroup: SensorGroup = JSON.parse(JSON.stringify(sensorGroup));
    newSensorGroup.createdDate = sensorGroup.createdDate;
    newSensorGroup.modifiedDate = sensorGroup.modifiedDate;
    return newSensorGroup;
  }

  
  mapSensorGroup2SensorGroupInfo(sensorGroup: SensorGroup): SensorGroupInfo {
    const newSensorGroupInfo: SensorGroupInfo = {
      id: sensorGroup.id,
      name: sensorGroup.name,
      description: sensorGroup.description,
      createdDate: sensorGroup.createdDate, 
      modifiedDate: sensorGroup.modifiedDate, 
      isDisabled: sensorGroup.isDisabled,
      crop: sensorGroup.crop,
      beginDate: sensorGroup.beginDate,
      endDate: sensorGroup.endDate,
      plantDate: sensorGroup.plantDate, 
      userName: sensorGroup.userName,
      userGroupID: sensorGroup.userGroupId,
      uuids: [],
      sensors: this.sensorMapper.mapSensors2SensorInfos(sensorGroup.sensors)
    };
    sensorGroup.sensors.forEach(s => newSensorGroupInfo.uuids.push(s.uuid));
    return newSensorGroupInfo;
  }

  //-----------------

  getEditChanges4SensorGroups(sensorGroup: SensorGroup, preEditObjectAsJSONString: string): string {
    let results = '';

    if (preEditObjectAsJSONString != '') {
      const original: SensorGroup = JSON.parse(preEditObjectAsJSONString);
      results += this.compareProperty('Name', original.name, sensorGroup.name);
      results += this.compareProperty('Description', original.description, sensorGroup.description);
      results += this.compareProperty('PlantDate', this.prepareProperty4String(original.plantDate), this.prepareProperty4String(sensorGroup.plantDate));
      results += this.compareProperty('Crop', original.crop, sensorGroup.crop);
      //results += this.common.compareProperty('SensorCount', original.sensors.length.toString(), sensorGroup.sensors.length.toString());
      results += this.checkSensorChanges(original.sensors, sensorGroup.sensors);
      results += this.getOwnerChanges4SensorGroups(original, sensorGroup);
      results += this.compareProperty('BeginDate', this.prepareProperty4String(original.beginDate), this.prepareProperty4String(sensorGroup.beginDate));
      results += this.compareProperty('EndDate', this.prepareProperty4String(original.endDate), this.prepareProperty4String(sensorGroup.endDate));
    }

    return results;
  }


  getOwnerChanges4SensorGroups(originalGroup: SensorGroup, newGroup: SensorGroup): string {
    if (originalGroup.userGroupId == undefined && newGroup.userGroupId == undefined) {    // Group didn't change, look for a user change
      if (originalGroup.userName != newGroup.userName) {
        return `Owner (${originalGroup.userName} -> ${newGroup.userName}),  `;
      }
    }

    if (originalGroup.userGroupId == undefined && newGroup.userGroupId != undefined) {    // Group changed
      return `Owner (self -> ${newGroup.userGroup}),  `;
    }

    if (originalGroup.userGroupId != undefined && newGroup.userGroupId == undefined) {    // Group changed
      return `Owner (${originalGroup.userGroup} -> self),  `;
    }

    if (originalGroup.userGroupId != undefined && newGroup.userGroupId != undefined && originalGroup.userGroupId != newGroup.userGroupId) {    // Group changed
      return `Owner (${originalGroup.userGroup} -> ${newGroup.userGroup}),  `;
    }

    return '';
  }

  //-----------

  getEditChanges4Sensors(sensor: Sensor, preEditObjectAsJSONString: string): string {
    let results = '';

    if (preEditObjectAsJSONString != undefined && preEditObjectAsJSONString != '') {
      const original: Sensor = JSON.parse(preEditObjectAsJSONString);
      results += this.compareProperty('Name', original.name, sensor.name);
      results += this.compareProperty('Type', original.type, sensor.type);
      results += this.compareProperty('Notes', original.notes, sensor.notes);
      results += this.getOwnerChanges4Sensors(original, sensor);
      results += this.compareProperty('UID', this.prepareProperty4String(original.uuid), this.prepareProperty4String(sensor.uuid));
      results += this.compareProperty('gpsLat', (original.gpsLocation != undefined) ? original.gpsLocation.gpsLat.toString() : '',
                                                        (sensor.gpsLocation != undefined) ? sensor.gpsLocation.gpsLat.toString() : '');
      results += this.compareProperty('gpsLong', (original.gpsLocation != undefined) ? original.gpsLocation.gpsLong.toString() : '',
                                                        (sensor.gpsLocation != undefined) ? sensor.gpsLocation.gpsLong.toString() : '');
      results += this.compareProperty('gpsAlt', (original.gpsLocation != undefined) ? original.gpsLocation.gpsAlt.toString() : '',
                                                        (sensor.gpsLocation != undefined) ? sensor.gpsLocation.gpsAlt.toString() : '');
      results += this.compareProperty('gpsDesc', (original.gpsLocation != undefined) ? original.gpsLocation.locationDescription : '',
                                                        (sensor.gpsLocation != undefined) ? sensor.gpsLocation.locationDescription : '');
      if (sensor.sensorCalibrationHistory) {
        sensor.sensorCalibrationHistory.forEach(x => {
          if (original.sensorCalibrationHistory.find(y => y.id == x.id) != undefined) {
            results += this.compareProperty('Calibration:' + x.sensorReadingTypeId, JSON.stringify(original.sensorCalibrationHistory.find(y => y.id == x.id).parameters), JSON.stringify(x.parameters));          
          }
        });
      }
    }
    else {
      results += this.compareProperty('Name', sensor.nameRevert, sensor.name);
      results += this.compareProperty('Type', sensor.brandRevert, sensor.type);
    }

    return results;
  }


  getOwnerChanges4Sensors(originalSensor: Sensor, newSensor: Sensor): string {
    if (originalSensor.userGroupId == undefined && newSensor.userGroupId == undefined) {    // Group didn't change, look for a user change
      if (originalSensor.userName != newSensor.userName) {
        return `Owner (${originalSensor.userName} -> ${newSensor.userName}),  `;
      }
    }

    if (originalSensor.userGroupId == undefined && newSensor.userGroupId != undefined) {    // Group changed
      return `Owner (self -> ${newSensor.userGroup}),  `;
    }

    if (originalSensor.userGroupId != undefined && newSensor.userGroupId == undefined) {    // Group changed
      return `Owner (${originalSensor.userGroup} -> self),  `;
    }

    if (originalSensor.userGroupId != undefined && newSensor.userGroupId != undefined && originalSensor.userGroupId != newSensor.userGroupId) {    // Group changed
      return `Owner (${originalSensor.userGroup} -> ${newSensor.userGroup}),  `;
    }

    return '';
  }

  //------------

  checkSensorChanges(origSensors: Sensor[], newSensors: Sensor[]): string {
    var response = "";

    origSensors.forEach(origUUID => {
      var index = newSensors.findIndex(newUUID => newUUID.uuid == origUUID.uuid);
      if (index < 0) {
        response += "Sensor removed (" + origUUID.uuid + "),  ";
      }
    });

    newSensors.forEach(newUUID => {
      var index = origSensors.findIndex(origUUID => origUUID.uuid == newUUID.uuid);
      if (index < 0) {
        response += "Sensor added (" + newUUID.uuid + "),  ";
      }
    });

    return response;
  }

  
  generateUUID(): string {
    let d = new Date().getTime();                                                     //Timestamp
    let d2 = (performance && performance.now && (performance.now()*1000)) || 0;       //Time in microseconds since page-load or 0 if unsupported
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
      let r = Math.random() * 16;                                                     //random number between 0 and 16
      if (d > 0) {                                                                    //Use timestamp until depleted
        r = (d + r)%16 | 0; // eslint-disable-line no-bitwise
        d = Math.floor(d/16);
      } else {                                                                        //Use microseconds since page-load if supported
        r = (d2 + r)%16 | 0; // eslint-disable-line no-bitwise
        d2 = Math.floor(d2/16);
      }
      // eslint-disable-next-line no-bitwise
      return (c === 'x' ? r : (r & 0x3 | 0x8)).toString(16);
    });
  }


  generateUUID_FromMacAddressAndSerial(macAddress: string, serial: string): string {
    var macAddressWithoutColons = macAddress.replace(/:/g, '');
    return "00000000-0000-0000-0000-" + macAddressWithoutColons.toUpperCase();
  }


  removeReservedCharsInActionOptions(actionOption: MediaActionsOptionsModel) {
    actionOption.Tags = actionOption.Tags.replace(/=/g, ',');
    actionOption.Tags = actionOption.Tags.replace(/;/g, ',');
    actionOption.Tags = actionOption.Tags.replace(/ /g, ',');
    actionOption.Tags = actionOption.Tags.replace(/#/g, ',');
    actionOption.Tags = actionOption.Tags.replace(/&/g, '_');
  }

  
  removeReservedCharsInPresets(cameraPreset: PTZCameraPreset) {
    cameraPreset.name = cameraPreset.name.replace(/=/g, '');
    cameraPreset.name = cameraPreset.name.replace(/;/g, '');
    cameraPreset.name = cameraPreset.name.replace(/ /g, '_');
    cameraPreset.name = cameraPreset.name.replace(/#/g, '');
    cameraPreset.name = cameraPreset.name.replace(/&/g, '_');
  }

  
  createNewLastReading(sensorId: number): LastSensorReading {
    var lastReading: LastSensorReading = {
      sensorId: sensorId,
      isLoaded: false,
      date: new Date(),
      displayColor: this.getColorCodeForLastReadingDate(new Date()),
      waitingForLoad: false
    }
    return lastReading;
  }
  

  getEventLogColor(eventAction: number | string): string {
    if (typeof(eventAction) === 'string') {
      eventAction = parseInt(eventAction, 10);
    }
    if (eventAction == 0) {
      return '#e3e3e3';   // Light gray
    } else if (eventAction == 1) {
      return '#a9d18e';  //'#00C851';  // Green
    } else if (eventAction == 2 || eventAction == 12 || eventAction == 7 || eventAction == 8) {
      return '#f6f728';  //'#f3f428';  // Yellow
    } else if (eventAction == 3 || eventAction == 13) {
      return '#e84848';  //'#FF0000';  // Red
    } else if (eventAction == 4) {
      return '#ff8130';  //'#F7680C';  // Dark orange
    } else if (eventAction == 5) {
      return '#ff9957';  //'#F78E12';  // Light orange
    } else if (eventAction == 6 || eventAction == 16) {
      return '#e84848';  //'#F78E12';  // Red
    } else if (eventAction == 11) {
      return '#b4c7e7';  //'#ADD8E6';  // Light blue
    } else {
      return 'clear';
    }
  }


  getEventLogResultColor(result: string): string {
    if (result == 'true') {
      return '#548235';  // Green
    } else {
      return 'black';
    }
  }


    buildCombinedConditionResults(eventLogInfo: EventLogInfo, sensorGroups: SensorGroup[], screenInnerWidth: number): EventLogResult[] {
      const results: EventLogResult[] = [];
      const eventType = Object.values(EventType)[eventLogInfo.eventType];
  
      if (eventType == EventType.SensorBased && eventLogInfo.sensorEventResultInfos != null) {
        let tempResult = '';
        this.eventMapper.sortSensorEventResultInfosByOrder(eventLogInfo.sensorEventResultInfos);
  
        for (let index = 0; index < eventLogInfo.sensorEventResultInfos.length; index++) {
          const e = eventLogInfo.sensorEventResultInfos[index];
  
          if (screenInnerWidth >= 1000) {
            tempResult = this.getNameForSensorGroupID(e.sensorGroupID, sensorGroups) + ' | ';
          }
  
          var sensorReadingType = this.getNameForSensorReadingTypeID(e.sensorGroupID, e.sensorReadingTypeID, sensorGroups);
          tempResult += sensorReadingType + ' ' + this.eventMapper.getComparatorAtIndex(e.comparator) + ' ' + e.threshold;
  
          if (index != eventLogInfo.sensorEventResultInfos.length - 1) {
            tempResult = tempResult + '  ' + Object.values(LogicOperator)[e.logicOperator].toLowerCase();
          }
  
          const eventLogResult: EventLogResult = {
            value: this.setValuePrecisionForKey(e.calculatedValue, sensorReadingType).toString(),
            //value: e.calculatedValue.toString(),
            text: tempResult,
            parentID: e.id,
            parentType: EventType.SensorBased
          };
          results.push(eventLogResult);
        }
        return results;
      }
  
      else if (eventType == EventType.TimerBased && eventLogInfo.timerEventResultInfos != null) {
        let tempResult = '';
        this.eventMapper.sortTimerEventResultInfosByOrder(eventLogInfo.timerEventResultInfos);
  
        for (let index = 0; index < eventLogInfo.timerEventResultInfos.length; index++) {
          const t = eventLogInfo.timerEventResultInfos[index];
  
          if (t.type == 0) {
            const newTime = moment.utc(t.timingSingle).local().format('HH:mm');
            const newDaysOfWeek = this.eventMapper.convertUTCDaysOfWeek2LocalString(t.timingSingle, newTime, t.daysOfWeek);
            tempResult = Object.values(TimerEventType)[t.type] + ' @ ' +  newTime  + ' (' + this.buildDaysOfWeekStringUserView(newDaysOfWeek) + ')';
          }
          else if (t.type == 1) {
            tempResult = Object.values(TimerEventType)[t.type] + ' ' + Object.values(TimingInterval)[t.timingRepeat] + ' (' + this.buildDaysOfWeekStringUserView(t.daysOfWeek) + ')';
          }
  
          if (index != eventLogInfo.timerEventResultInfos.length - 1) {
            tempResult = tempResult + '  ' + Object.values(LogicOperator)[t.logicOperator].toLowerCase();
          }
  
          const eventLogResult: EventLogResult = {
            value: (t.result ? 'true' : 'false'),
            text: tempResult,
            parentID: t.id,
            parentType: EventType.TimerBased
          };
          results.push(eventLogResult);
        }
        return results;
      } else {
        const eventLogResult: EventLogResult = {
          value: '0',
          text: 'not evaluated',
          parentID: -1,
          parentType: null
        };
        results.push(eventLogResult);
        return results;
      }
    }
  

    buildCombinedActionResults(eventLogInfo: EventLogInfo): EventLogResult[] {
      if (eventLogInfo == null) {
        return;
      }
      const results: EventLogResult[] = [];
      if (eventLogInfo.actionResultInfos == null || eventLogInfo.actionResultInfos.length == 0) {
        let errorCode = -1;
        if ( eventLogInfo.eventType == 0 && eventLogInfo.sensorEventResultInfos != null) {
          const sensorEventWithCode = eventLogInfo.sensorEventResultInfos.find(s => s.errorCode != 0);
          if (sensorEventWithCode != null) {
            errorCode = sensorEventWithCode.errorCode;
          }
        }
        const eventLogResult: EventLogResult = {
          value: errorCode.toString(),
          text: this.getEventResultsText(errorCode),
          parentID: -1,
          parentType: null
        };
        results.push(eventLogResult);
        return results;
      }
  
      eventLogInfo.actionResultInfos.forEach(a => {
        const eventLogResult: EventLogResult = {
          value: a.result,
          text: Object.values(ActionType)[a.type] + ' - ' + this.getEventResultsText(parseInt(a.result, 10)),
          parentID: -1,
          parentType: null
        };
        results.push(eventLogResult);
      });
      return results;
    }
  
  
    buildDaysOfWeekStringUserView(daysOfWeek: string): string {
      let result = '';
  
      result += daysOfWeek[0] == '1' ? 'Su ' : '';
      result += daysOfWeek[1] == '1' ? 'M ' : '';
      result += daysOfWeek[2] == '1' ? 'Tu ' : '';
      result += daysOfWeek[3] == '1' ? 'W ' : '';
      result += daysOfWeek[4] == '1' ? 'Th ' : '';
      result += daysOfWeek[5] == '1' ? 'F ' : '';
      result += daysOfWeek[6] == '1' ? 'Sa ' : '';
  
      return result;
    }
  
  
    getNameForSensorGroupID(sensorGroupId: number, sensorGroups: SensorGroup[]): string {
      const sensorGroup = sensorGroups.find(x => x.id == sensorGroupId);
      return sensorGroup != null ? sensorGroup.name : '';
    }
  
  
    getNameForSensorReadingTypeID(sensorGroupId: number, readingTypeId: number, sensorGroups: SensorGroup[]): string {
      const readingType = this.getReadingTypesForSensorGroupId(sensorGroupId, sensorGroups).find(x => x.id == readingTypeId);
      return readingType != null ? readingType.type : '';
    }
  
  
    getReadingTypesForSensorGroupId(sensorGroupId: number, sensorGroups: SensorGroup[]): SensorReadingType[] {
      var sensorReadingTypes: SensorReadingType[] = [];
      const sensorGroup = sensorGroups.find(x => x.id == sensorGroupId);
  
      if (sensorGroup == undefined || sensorGroup.sensors == undefined || sensorGroup.sensors.length < 1) {
        return sensorReadingTypes;
      }
  
      sensorGroup.sensors.forEach(x => {
        x.sensorReadingTypes.forEach(y => {
          if (sensorReadingTypes.find(z => z.id == y.id) == undefined) {
            var readingType: SensorReadingType = {
              id: y.id,
              type: y.type,
              checked: false
            }
            sensorReadingTypes.push(readingType);
          }
        });
      });
  
      return sensorReadingTypes;
    }
  
  
    getEventResultsText(eventAction: number): string {
      if (eventAction == -1) {
        return 'no action';
      } else if (eventAction == 0) {
        return 'Waiting';
      } else if (eventAction == 1) {
        return 'Triggered-Success';
      } else if (eventAction == 2) {
        return 'Triggered-Ignored';
      } else if (eventAction == 3) {
        return 'Triggered-Error';
      } else if (eventAction == 4) {
        return 'Not enough data';
      } else if (eventAction == 5) {
        return 'No recent data';
      } else if (eventAction == 6) {
        return 'Timed out';
      } else if (eventAction == 7) {
        return 'Not scheduled';
      } else if (eventAction == 8) {
        return 'Paused';
      } else if (eventAction == 9) {
        return 'Queued';
      } else if (eventAction == 11) {
        return 'Manual-Success';
      } else if (eventAction == 12) {
        return 'Manual-Ignored';
      } else if (eventAction == 13) {
        return 'Manual-Error';
      } else if (eventAction == 16) {
        return 'Manual-Timed out';
      } else if (eventAction == 17) {
        return 'Manual-Queued'
      } else {
        return 'Unknown result';
      }
    }
  
    
    setTimeZoneSelection(timeZone: string): [TimeZoneDisplay, number] {
      switch (timeZone) {
        case 'UTC':
          return [TimeZoneDisplay.UTC, 0];
  
          case 'Local':
            return [TimeZoneDisplay.Local, moment().utcOffset()];
  
          case 'Source':
            return [TimeZoneDisplay.Source, 0];
  
        default:
          return [TimeZoneDisplay.Local, moment().utcOffset()];
      }
    }


  sortActionQueueByColumn(queue: ActionQueueLive[], columnField: string, ascending: boolean = true): void {
    queue.sort((a, b) => {
      if (b[columnField] > a[columnField]) {
        return (ascending ? -1 : 1);
      }
      if (b[columnField] < a[columnField]) {
        return (ascending ? 1 : -1);
      }
      return 0;
    });
  }


  getEventActionLabelForEvent(event: Event): string {
    if (event.eventActions == null || event.eventActions.length == 0) {
      return 'No Actions';
    }

    if (event.eventActions.length > 1) {
      return `multiple({${event.eventActions.length})`;
    }

    const action = event.eventActions[0];

    return event.eventActions[0].type.toString();
  } 


  setStatusColorForActionQueueLiveItem(actionQueueLiveItem: ActionQueueLive): void {
    switch (actionQueueLiveItem.status.toLowerCase()) {
      case 'waiting':
        actionQueueLiveItem.statusFontColor = 'lightskyblue';
        actionQueueLiveItem.statusBgColor = 'clear';
        break;
      case 'active':
        actionQueueLiveItem.statusFontColor = 'black';
        actionQueueLiveItem.statusBgColor = '#a9d18e';
        break;
      case 'canceled':
        actionQueueLiveItem.statusFontColor = 'orange';
        actionQueueLiveItem.statusBgColor = 'clear';
        break;
      default:
        actionQueueLiveItem.statusFontColor = 'black';
        actionQueueLiveItem.statusBgColor = 'clear';
        break;
    }

    if (actionQueueLiveItem.ruleUsed == null || actionQueueLiveItem.ruleUsed == '') {
      actionQueueLiveItem.ruleUsedFontColor = 'black';
      actionQueueLiveItem.ruleUsedBgColor = 'clear';
      return;
    }
    
    switch (actionQueueLiveItem.ruleUsed.toLowerCase()) {
      case 'batch-filled':
        actionQueueLiveItem.ruleUsedFontColor = 'rgb(13 110 253)';
        actionQueueLiveItem.ruleUsedBgColor = 'clear';
        break;
      default:
        actionQueueLiveItem.ruleUsedFontColor = 'black';
        actionQueueLiveItem.ruleUsedBgColor = 'clear';
        break;
    }

  }

  
  getFormattedSensorCalibrationParameters(templateId: number, params: string): string {
    let formattedParams = '';

    try {
      const paramsObj = JSON.parse(params);

      if (templateId === 0) {
        formattedParams = `${paramsObj[0]}\n${paramsObj[1]}`;
      }
      else if (templateId === 1) {
        formattedParams = `${paramsObj[0]}\n${paramsObj[1]}\n${paramsObj[2]}`;
      }
      else if (templateId === 2) {
        formattedParams = `${paramsObj[0]}\n${paramsObj[1]}\n${paramsObj[2]}\n${paramsObj[3]}`;
      }
      else {
        formattedParams = 'none';
      }
    }
    catch (e) {
      formattedParams = params;
      console.error('Error parsing calibration parameters JSON string:', e);
    }

    return formattedParams.trim();
  }


}

